import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, Eye, User, ArrowLeft, Share2 } from 'lucide-react';
import Markdown from 'markdown-to-jsx';

const NewsletterDetail = () => {
  const { id } = useParams();
  const [newsletter, setNewsletter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNewsletter = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/newsletter/${id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!response.ok) {
          throw new Error('Newsletter not found');
        }

        const data = await response.json();
        setNewsletter(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchNewsletter();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (error || !newsletter) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error</h2>
          <p className="text-gray-600">{error || 'Newsletter not found'}</p>
          <a href="/college/newsletters" className="mt-4 inline-flex items-center text-blue-600 hover:text-blue-700">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Newsletters
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Back Button */}
        <div className="mb-8">
          <a href="/newsletters" className="inline-flex items-center text-gray-600 hover:text-gray-900">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Newsletters
          </a>
        </div>

        {/* Newsletter Content */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          {newsletter.coverImage && (
            <img
              src={newsletter.coverImage}
              alt={newsletter.title}
              className="w-full h-64 object-cover object-center"
            />
          )}

          <div className="p-8">
            {/* Metadata */}
            <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-gray-500">
              <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full">
                {newsletter.category}
              </span>
              <span className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                {new Date(newsletter.createdAt).toLocaleDateString()}
              </span>
              <span className="flex items-center">
                <Eye className="w-4 h-4 mr-2" />
                {newsletter.viewCount} views
              </span>
              {newsletter.college && (
                <span>{newsletter.college.name}</span>
              )}
            </div>

            {/* Title and Subtitle */}
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {newsletter.title}
            </h1>
            {newsletter.subtitle && (
              <p className="text-xl text-gray-600 mb-6">
                {newsletter.subtitle}
              </p>
            )}

            {/* Main Content */}
            <div className="prose max-w-none">
              <Markdown
                options={{
                  overrides: {
                    p: {
                      component: props => <p className="text-gray-800 mb-4" {...props} />
                    },
                    strong: {
                      component: props => <strong className="font-bold" {...props} />
                    },
                    em: {
                      component: props => <em className="italic" {...props} />
                    },
                    ul: {
                      component: props => <ul className="list-disc pl-6 mb-4 space-y-2" {...props} />
                    },
                    ol: {
                      component: props => <ol className="list-decimal pl-6 mb-4 space-y-2" {...props} />
                    },
                    li: {
                      component: props => <li className="text-gray-800" {...props} />
                    },
                    h1: {
                      component: props => <h1 className="text-4xl font-bold mb-6" {...props} />
                    },
                    h2: {
                      component: props => <h2 className="text-3xl font-bold mb-5" {...props} />
                    },
                    h3: {
                      component: props => <h3 className="text-2xl font-bold mb-4" {...props} />
                    },
                    h4: {
                      component: props => <h4 className="text-xl font-bold mb-3" {...props} />
                    },
                    a: {
                      component: props => <a className="text-blue-600 hover:text-blue-700 underline" {...props} />
                    },
                    span: {
                      component: 'span'  // This preserves the span elements with their styles
                    }
                  },
                  forceBlock: true,
                  forceWrapper: true
                }}
              >
                {newsletter.content || ''}
              </Markdown>
            </div>

            {/* Share Button */}
            <div className="mt-8 pt-8 border-t">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  alert('Link copied to clipboard!');
                }}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share Newsletter
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsletterDetail;